import monitor from "../../utils/monitoring";
import { apiFetch } from "@/services/api/restApi";
import {
  Account,
  AccountNextStep,
  UIQuilterState,
  NEUiInfo,
  NotifierRoles,
  NotifierRolesByServer,
  Person,
  ServiceProvider,
} from "@/types";
import { isComplete as isAccountsComplete } from "./Accounts";
import { isComplete as isDeceasedDetailsComplete } from "./DeceasedDetails";
import { isComplete as isKYCComplete } from "./KYC";
import { isNotifierFormComplete } from "./NotifierDetails";
import { isNokFormComplete } from "./NokForm";
import { isExecutorFormComplete } from "./ExecutorForm";
import { Section } from "@/Sections";
import { ui_state_machine } from "@/utils/Intestacy";
import { isDocumentsComplete, neDocumentRules } from "@/helpers/documents-helpers";
import {
  doesSPSRequireGOR,
  doesSPSRequireIntestacy,
  isQuilter,
} from "@/helpers/service-provider-helpers";

export const getCase = async (args: { caseId: string; signature: string | null }) => {
  const result = await getCaseRequest(args);

  if (result.error) {
    switch (result.error) {
      default:
        throw new Error("Unexpected error; please contact customer support");
    }
  }
  return result.data;
};

const getCaseRequest = async (args: { caseId: string; signature: string | null }) => {
  const { caseId, signature } = args;

  let result;
  try {
    result = await apiFetch({
      path: `/notifier/cases/${caseId}`,
      search: !signature ? {} : { caseId, sig: signature },
    });
  } catch (error) {
    monitor.logError({
      event: "GET /notifier/cases/{caseId}",
      args,
      error,
    });
    throw error;
  }

  return result;
};

export const notifierEmailAddressVerified = (persons: Person[], bypass?: boolean) => {
  if (persons) {
    const notifier = persons.find((p) => p.roles?.includes(NotifierRolesByServer.Notifier));
    return notifier?.emailAddressVerified || bypass || false;
  }
  return bypass || false;
};

export const NEFormMenuEntries = ({
  collectNokDetails,
  collectExecutorDetails,
  bypassKYC,
}: any) => {
  return [
    { key: Section.Accounts, label: "Add accounts" },
    { key: Section.Deceased, label: "Person who died" },
    { key: Section.Notifier, label: "Your Details" },
    ...(collectNokDetails ? [{ key: Section.Nok, label: "Next of kin Details" }] : []),
    ...(collectExecutorDetails ? [{ key: Section.Executor, label: "Executor Details" }] : []),
    { key: Section.Documents, label: "Documents" },
    ...(bypassKYC ? [] : [{ key: Section.KYC, label: "Fraud Protection" }]),
    { key: Section.Submit, label: "Review & submit" },
  ];
};

export function getFormCompletionState(
  serviceProviders: any,
  form: any,
  {
    collectNokDetails,
    collectExecutorDetails,
    willExists,
    intestacyFlow,
    quilter,
    notifierRole,
    spsRequireGOR,
  }: NEUiInfo
) {
  const serviceProvidersMap = serviceProviders.reduce((acc: any, serviceProvider: any) => {
    if (serviceProvider.id !== undefined) {
      acc[serviceProvider.id] = serviceProvider;
    }
    return acc;
  }, {} as Record<string, ServiceProvider>);

  const documentRules = neDocumentRules(form, serviceProvidersMap, {
    intestacyFlow,
    willExists,
    quilter,
    notifierRole,
    spsRequireGOR,
  });

  return {
    [Section.Accounts]: !!form.submittedSections.accounts && isAccountsComplete(form),
    [Section.Deceased]: !!form.submittedSections.deceased && isDeceasedDetailsComplete(form),
    [Section.Notifier]:
      !!form.submittedSections.notifier && isNotifierFormComplete(form, intestacyFlow),
    [Section.Nok]: collectNokDetails && !!form.submittedSections.nok && isNokFormComplete(form),
    [Section.Executor]:
      collectExecutorDetails && !!form.submittedSections.executor && isExecutorFormComplete(form),
    [Section.Documents]:
      !!form.submittedSections.documents && isDocumentsComplete(form, documentRules),
    [Section.KYC]: !!form.submittedSections.kyc && isKYCComplete(form),
    [Section.Submit]: form.submitted,
  };
}

export function onCaseLoadUI(
  notifier: Person,
  willAvailable: boolean,
  bypassKYC: boolean,
  accounts: Array<Account>,
  serviceProviders: Array<ServiceProvider>
): Partial<NEUiInfo> {
  // Corrected: Use map and filter to collect companyIds
  const companyIds = accounts
    .map((account: any) => account.companyId)
    .filter((id: any) => id !== undefined && id !== null);

  const intestacyFlow: boolean = doesSPSRequireIntestacy(companyIds, serviceProviders);

  // Corrected: Initialize state as an empty object to prevent spreading undefined
  let state = {};
  if (intestacyFlow) {
    state = ui_state_machine(notifier, willAvailable);
  }
  return {
    ...state,
    intestacyFlow,
    thirdPartyMarketing: !!notifier.thirdPartyMarketing,
    bypassKYC,
    notifierRole: notifier?.role as NotifierRoles,
    quilter: neQuilterState(accounts),
    spsRequireGOR: doesSPSRequireGOR(companyIds, serviceProviders),
  };
}

// neQuilterState.ts
export function neQuilterState(accounts: Account[]): UIQuilterState {
  let isQuilterSP = false;
  let quilterThresholdReached = false;
  let withdrawal = false;
  let isPlaceOfDeathUk = true;

  if (accounts && accounts.length !== 0) {
    for (const account of accounts) {
      // isQuilter(...) is assumed to be a function checking account details
      isQuilterSP = isQuilterSP || isQuilter(account.companyId);
      quilterThresholdReached = quilterThresholdReached || !!account.assetsValueAboveThreshold;
      withdrawal =
        withdrawal || account.nextSteps === AccountNextStep.WithdrawalOfFundsToExecutorAccount;
      if (account.isPlaceOfDeathUk !== undefined) {
        isPlaceOfDeathUk = account.isPlaceOfDeathUk;
      }
    }
  }

  return {
    isQuilter: isQuilterSP,
    quilterThresholdReached,
    [AccountNextStep.WithdrawalOfFundsToExecutorAccount]: withdrawal,
    isPlaceOfDeathUk,
  };
}
